import React from 'react'
import dayjs from 'dayjs'

/**
 * Format currency as PHP (Philippine Peso)
 * @param {number} amount - Amount to format
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-PH', {
    style: 'currency',
    currency: 'PHP',
    minimumFractionDigits: 2
  }).format(amount)
}

/**
 * Get unique values from an array of objects for a specific field
 * @param {Array} data - Array of objects
 * @param {string} field - Field to extract unique values from
 * @returns {Array} Array of unique values
 */
export const getUniqueValues = (data, field) => {
  return [...new Set(data.map(item => item[field]).filter(value => value && value.trim() !== ''))]
}

/**
 * Get S3 path for document based on available properties
 * @param {Object} doc - Document object with properties
 * @param {string} transactionId - ID of the transaction
 * @returns {string|null} S3 path or null if path cannot be determined
 */
export const getAttachmentS3Path = (doc, transactionId) => {
  if (doc.url && doc.url.includes('amazonaws.com')) {
    return doc.url
  } else if (doc.s3Location) {
    return doc.s3Location.startsWith('http')
      ? doc.s3Location
      : `https://kol-dev-cash-management-bucket.s3.ap-southeast-2.amazonaws.com/${doc.s3Location}`
  } else if (doc.name) {
    return `https://kol-dev-cash-management-bucket.s3.ap-southeast-2.amazonaws.com/pettyCash/attachments/PTCTXN-${transactionId || '00000000'}/${doc.name}`
  }
  return null
}

/**
 * Get column search props for text search
 * Fixed to properly handle reset functionality
 * @param {string} dataIndex - Column data index
 * @param {string} title - Column title
 * @param {Component} Input - Input component
 * @param {Component} Button - Button component
 * @param {Component} SearchOutlined - Search icon component
 * @returns {Object} Column search props object
 */
export const getColumnSearchProps = (dataIndex, title, Input, Button, SearchOutlined) => ({
  filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
    <div style={{ padding: 8 }} onClick={(e) => e.stopPropagation()}>
      <Input
        placeholder={`Search ${title}`}
        value={selectedKeys[0]}
        onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
        onPressEnter={() => confirm({ closeDropdown: true })}
        style={{ marginBottom: 8, display: 'block' }}
      />
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button
          type="primary"
          onClick={() => confirm({ closeDropdown: true })}
          icon={<SearchOutlined />}
          size="small"
          style={{ width: 90 }}
        >
          Search
        </Button>
        <Button
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()

            if (clearFilters) {
              clearFilters()
            }

            setSelectedKeys([])

            confirm({ closeDropdown: true, clearFilters: true })
          }}
          size="small"
          style={{ width: 90 }}
        >
          Reset
        </Button>
      </div>
    </div>
  ),
  filterIcon: filtered => (
    <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
  ),
  onFilter: (value, record) => {
    // If no value or empty string, don't filter - show all records
    if (!value || value === '') {
      return true
    }

    // Handle cases where record[dataIndex] might be null or undefined
    if (record[dataIndex] === null || record[dataIndex] === undefined) {
      return false
    }

    // Convert both record value and search value to lowercase string for comparison
    return record[dataIndex]
      .toString()
      .toLowerCase()
      .includes(value.toLowerCase())
  }
})

/**
 * Get custom filter with search capability
 * Fixed to properly handle reset functionality
 * @param {string} dataIndex - Column data index
 * @param {Array} options - Array of filter options
 * @param {Component} Input - Input component
 * @param {Component} Button - Button component
 * @param {Component} SearchOutlined - Search icon component
 * @param {Component} FilterOutlined - Filter icon component
 * @param {Component} Checkbox - Checkbox component
 * @returns {Object} Custom filter props object
 */

const CustomFilterDropdown = ({ dataIndex, options, selectedKeys, setSelectedKeys, confirm, clearFilters, Input, Button, SearchOutlined, Checkbox}) => {
  const [searchText, setSearchText] = React.useState('')
  const effectiveOptions = options.length > 0 ? options : ['Loading options...']
  const filteredOptions = effectiveOptions.filter(option => {
    if (!option) return false
    return option.toString().toLowerCase().includes(searchText.toLowerCase())
  })

  return (
    <div
      style={{
        padding: 16,
        background: '#fff',
        borderRadius: 4,
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        width: 240
      }}
      onClick={e => e.stopPropagation()}
    >
      <Input
        placeholder={`Search ${dataIndex}...`}
        value={searchText}
        onChange={e => setSearchText(e.target.value)}
        style={{ marginBottom: 8 }}
        prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
      />

      <div style={{ maxHeight: '200px', overflow: 'auto' }}>
        <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
          {filteredOptions.map((option, index) => (
            <div key={option || `empty-${index}`} style={{ margin: '8px 0' }}>
              <Checkbox
                checked={selectedKeys.includes(option)}
                onChange={e => {
                  const newSelectedKeys = e.target.checked
                    ? [...selectedKeys, option]
                    : selectedKeys.filter(key => key !== option)
                  setSelectedKeys(newSelectedKeys)
                }}
                style={{ width: '100%', display: 'flex', alignItems: 'center' }}
              >
                <span style={{ marginLeft: 8 }}>{option || '(Empty)'}</span>
              </Checkbox>
            </div>
          ))}
        </div>
      </div>

      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        marginTop: 16 
        }}>
        <Button
          onClick={e => {
            e.preventDefault()
            e.stopPropagation()
            if (clearFilters) clearFilters()
            setSelectedKeys([])
            setSearchText('')
            confirm({ closeDropdown: true, clearFilters: true })
          }}
          size="small"
        >
          Reset
        </Button>
        <Button
          type="primary"
          onClick={(e) => {
            e.stopPropagation()
            confirm({ closeDropdown: true })
          }}
          size="small"
        >
          OK
        </Button>
      </div>
    </div>
  )
}

export const getCustomFilterWithSearch = (
  dataIndex,
  options,
  Input,
  Button,
  SearchOutlined,
  FilterOutlined,
  Checkbox
) => {
  return {
    filterDropdown: (props) => (
      <CustomFilterDropdown
        {...props}
        dataIndex={dataIndex}
        options={options}
        Input={Input}
        Button={Button}
        SearchOutlined={SearchOutlined}
        Checkbox={Checkbox}
      />
    ),
    filterIcon: filtered => (
      <FilterOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
    ),
    onFilter: (value, record) => {
      if (!value) return true
      if (record[dataIndex] == null) return value === null || value === ''
      if (value === '----') return record[dataIndex] === '----' || !record[dataIndex]
      return record[dataIndex].toString() === value.toString()
    }
  }
}

/**
 * Get date filter props with correct date range filtering
 * Fixed to properly handle reset functionality and selected month
 * @param {Component} RangePicker - DatePicker.RangePicker component
 * @param {Component} Button - Button component
 * @param {Component} SearchOutlined - Search icon component
 * @param {dayjs} selectedDate - Selected date from MonthPicker
 * @returns {Object} Date filter props object
 */
export const getDateFilterProps = (RangePicker, Button, SearchOutlined, selectedDate) => ({
  filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
    <div style={{ padding: 8 }} onClick={(e) => e.stopPropagation()}>
      <RangePicker
        value={selectedKeys[0]}
        onChange={(dates) => {
          setSelectedKeys(dates ? [dates] : [])
        }}
        disabledDate={(current) => {
          // Use selectedDate to determine the valid range
          const start = selectedDate ? selectedDate.startOf('month') : dayjs().startOf('month')
          const end = selectedDate ? selectedDate.endOf('month') : dayjs().endOf('month')
          return current && (current < start || current > end)
        }}
        defaultPickerValue={
          selectedDate
            ? [selectedDate.startOf('month'), selectedDate.endOf('month')]
            : [dayjs().startOf('month'), dayjs().endOf('month')]
        }
        onOpenChange={(open) => {
          if (open) {
            // Reset calendar view to selected month when picker opens
            setSelectedKeys(
              selectedKeys[0] || [
                selectedDate ? selectedDate.startOf('month') : dayjs().startOf('month'),
                selectedDate ? selectedDate.endOf('month') : dayjs().endOf('month')
              ]
            )
          }
        }}
        dropdownClassName="hide-range-picker-navigation"
        style={{ marginBottom: 8, display: 'block', width: '100%' }}
      />
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button
          type="primary"
          onClick={() => confirm({ closeDropdown: true })}
          icon={<SearchOutlined />}
          size="small"
          style={{ width: 90 }}
        >
          Search
        </Button>
        <Button
          onClick={(e) => {
            // Prevent any default behavior or event bubbling
            e.preventDefault()
            e.stopPropagation()


            // Clear filters properly
            if (clearFilters) {
              clearFilters()
            }

            // Reset selected keys
            setSelectedKeys([])

            // Important: Confirm with explicit parameters to force table refresh
            confirm({ closeDropdown: true, clearFilters: true })
          }}
          size="small"
          style={{ width: 90 }}
        >
          Reset
        </Button>
      </div>
    </div>
  ),
  filterIcon: (filtered) => (
    <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
  ),
  onFilter: (value, record) => {
    // Return true for all records when no date range is selected (for reset functionality)
    if (!value || !value[0] || !value[1]) {
      return true
    }

    // Get start and end dates from the date picker
    const startDate = value[0].startOf('day')
    const endDate = value[1].endOf('day')
    // Parse the record's date with dayjs
    const recordDate = dayjs(record.date)

    // Check if the record's date is within the selected range (inclusive)
    return (
      (recordDate.isAfter(startDate) || recordDate.isSame(startDate)) &&
      (recordDate.isBefore(endDate) || recordDate.isSame(endDate))
    )
  }
})

/**
 * Get responsive table props based on screen size
 * @param {Array} columns - Table columns configuration
 * @returns {Object} Table props object
 */
export const getResponsiveTableProps = (columns) => {
  const isMobile = window.innerWidth <= 992
  return {
    scroll: {
      y: 'calc(100vh - 620px)', // Reduced height to accommodate independent footer
      x: 'max-content'
    },
    sticky: isMobile ? false : { offsetHeader: 0 },
    // Remove fixed properties for mobile view
    columns: isMobile
      ? columns.map((col) => ({ ...col, fixed: undefined }))
      : columns
  }
}